import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  AddPlus2,
  ChargerEV,
  CheckMark,
  ChevronDown,
  ChevronUp,
  ErrorWarningCircle,
  ExitDeleteRemoveClose,
  Info,
  PromotionStepComplete,
  PromotionStepOne,
} from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import { ChargerSelection, ChargerSelectionList } from '../../../stores/types/chargers.interface';
import { PromotionDetailsSectionProps } from '../../../stores/types/promotionForm.interface';
import { ComputedPromotionStatus } from '../../../stores/types/promotions.interface';
import {
  Button,
  ButtonSize,
  ButtonType,
  ColorType,
  Dropdown,
  DropdownType,
  FormInput,
  Icon,
  Label,
  LabelType,
} from '../../_ui';
import { CustomInputType, DateTimePicker } from '../../_ui/date-picker/Date-Picker.component';
import { InputType, ValidationType } from '../../_ui/Input.component';
import { toLocalDateString } from '../promotion.utils';
import PromotionStatus from '../PromotionStatus.component';
import { PromotionDetailsReadOnly } from './PromotionDetailsReadOnly.component';
import {
  ALL_CHARGERS_ID,
  LANGUAGE_ADD_KEY,
  LANGUAGE_LABEL_KEY,
  OPTIONAL_PROMOTION_LANGUAGES,
  PROMOTION_LANGUAGES,
  PromotionLanguage,
  RECEIPT_LABEL_MAX_LENGTH,
} from './promotionForm.constants';

export const PromotionDetailsSection = ({
  state,
  errors,
  isExpanded,
  isSaving,
  isReadOnly,
  status,
  onToggleExpand,
  onChange,
  onClearError,
  onSave,
  onShowSecondaryLanguage,
  onHideSecondaryLanguage,
}: PromotionDetailsSectionProps) => {
  const { t } = useTranslation();

  const renderError = useCallback(
    (message: string | undefined) =>
      message ? (
        <Label
          className='pt-1'
          type={LabelType.LABEL_S_MEDIUM}
          color={ColorType.NEGATIVE}
          text={message}
        />
      ) : null,
    [],
  );

  const handleChargersChange = (items: ChargerSelectionList[]) => {
    const selectableGroups = items.filter(
      (group: ChargerSelectionList) => group.id !== ALL_CHARGERS_ID,
    );
    const allOthersSelected =
      selectableGroups.length > 0 &&
      selectableGroups.every((group: ChargerSelectionList) =>
        group.children.every((charger: ChargerSelection) => charger.selected),
      );

    const normalized = items.map((group: ChargerSelectionList) =>
      group.id === ALL_CHARGERS_ID ? { ...group, selected: allOthersSelected } : group,
    );

    onChange((prev) => ({ ...prev, chargers: normalized }));
    onClearError('chargers');
  };

  const handleDiscountValueChange = (raw: string) => {
    onChange((prev) => ({ ...prev, discountValue: raw }));
    onClearError('discountValue');
  };

  const renderLanguageInput = (lang: PromotionLanguage, isOptional: boolean) => {
    const value = state.labels[lang];
    const error = errors.labels[lang];
    const characterCount = value.length;

    return (
      <div key={lang} className='flex flex-col gap-2 w-full max-w-[440px]'>
        <Label
          text={t(LANGUAGE_LABEL_KEY[lang])}
          type={LabelType.LABEL_S}
          color={ColorType.GREY6}
        />
        <div className='flex flex-row gap-2 items-center'>
          <FormInput
            defaultValue={value}
            placeholder={t(`add_${lang}_label_placeholder`)}
            inputType={InputType.TEXT}
            validationType={ValidationType.NONE}
            maxLen={`${RECEIPT_LABEL_MAX_LENGTH}`}
            width='100%'
            hasError={Boolean(error)}
            onChange={(event) => {
              onChange((prev) => ({
                ...prev,
                labels: { ...prev.labels, [lang]: event.target.value },
              }));
              onClearError(lang);
            }}
            dataTestId={`promotionLabel-${lang}`}
          />
          {isOptional && (
            <Icon
              src={ExitDeleteRemoveClose}
              size={IconSize.SIZE_20x20}
              color={ColorType.GREY5}
              onClick={() => onHideSecondaryLanguage(lang)}
              alt={t('icon_alt_remove')}
              dataTestId={`removeLabel-${lang}`}
            />
          )}
        </div>
        {renderError(error)}
        <Label
          text={`${characterCount}/${RECEIPT_LABEL_MAX_LENGTH}`}
          type={LabelType.LABEL_XS_MEDIUM}
          color={ColorType.GREY5}
        />
      </div>
    );
  };

  const renderHeader = () => (
    <button
      type='button'
      className='flex flex-row items-center justify-between w-full min-h-[68px] px-5 py-4 bg-grey1 rounded-t-lg'
      onClick={onToggleExpand}
      data-testid='promotionDetailsHeader'
    >
      <div className='flex flex-row items-center gap-3'>
        <Icon
          src={status === 'DELETED' ? CheckMark : status ? PromotionStepComplete : PromotionStepOne}
          size={IconSize.SIZE_24x24}
        />
        <div className='flex flex-col items-start gap-1'>
          <Label text={t('promotion_details')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
          <div className='flex flex-row items-center gap-2'>
            <Label text={state.name || '—'} type={LabelType.BODY3} color={ColorType.GREY6} />
            <PromotionStatus status={status ?? ComputedPromotionStatus.DRAFT} isLoading={false} />
          </div>
        </div>
      </div>
      <Icon
        src={isExpanded ? ChevronUp : ChevronDown}
        size={IconSize.SIZE_24x24}
        alt={isExpanded ? t('icon_alt_collapse') : t('icon_alt_expand')}
      />
    </button>
  );

  if (!isExpanded) {
    return <div className='border border-grey2 rounded-lg overflow-hidden'>{renderHeader()}</div>;
  }

  if (isReadOnly) {
    return (
      <div className='border border-grey2 rounded-lg overflow-hidden'>
        {renderHeader()}
        <PromotionDetailsReadOnly state={state} errors={errors} />
      </div>
    );
  }

  return (
    <div className='border border-grey2 rounded-lg overflow-hidden'>
      {renderHeader()}
      <div className='flex flex-col divide-y divide-grey2 px-5 bg-white'>
        <div className='flex flex-col gap-2 py-5'>
          <Label text={t('promotion_name')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
          <div className='w-full max-w-[440px]'>
            <FormInput
              defaultValue={state.name}
              placeholder={t('add_promotion_name')}
              inputType={InputType.TEXT}
              width='100%'
              maxLen='255'
              hasError={Boolean(errors.name)}
              onChange={(event) => {
                onChange((prev) => ({ ...prev, name: event.target.value }));
                onClearError('name');
              }}
              dataTestId='promotionName'
            />
            {renderError(errors.name)}
          </div>
        </div>

        <div className='flex flex-col gap-3 py-5'>
          <div className='flex flex-col gap-1'>
            <Label text={t('window')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
            <Label
              text={t('define_when_promotion_active')}
              type={LabelType.BODY3}
              color={ColorType.GREY6}
            />
          </div>
          <div className='flex flex-row gap-3 w-full max-w-[440px]'>
            <div className='flex flex-col gap-2 flex-1'>
              <Label
                text={t('promotion_starts')}
                type={LabelType.LABEL_S}
                color={ColorType.GREY6}
              />
              <DateTimePicker
                defaultSelectedDate={state.validFrom ? toLocalDateString(state.validFrom) : ''}
                format='MM/dd/yyyy'
                customInputType={CustomInputType.FORMINPUTDATE}
                showDefaultDate={false}
                customFormInputDivWidth='w-full'
                onChange={(date: Date | null) => {
                  onChange((prev) => ({ ...prev, validFrom: date }));
                  onClearError('validFrom');
                }}
              />
              {renderError(errors.validFrom)}
            </div>
            <div className='flex flex-col gap-2 flex-1'>
              <div className='flex flex-row gap-1'>
                <Label
                  text={t('promotion_ends')}
                  type={LabelType.LABEL_S}
                  color={ColorType.GREY6}
                />
                <Label
                  text={`(${t('optional')})`}
                  type={LabelType.LABEL_S_MEDIUM}
                  color={ColorType.GREY6}
                />
              </div>
              <DateTimePicker
                defaultSelectedDate={state.validUntil ? toLocalDateString(state.validUntil) : ''}
                format='MM/dd/yyyy'
                customInputType={CustomInputType.FORMINPUTDATE}
                showDefaultDate={false}
                customFormInputDivWidth='w-full'
                onChange={(date: Date | null) => {
                  if (date) {
                    date.setHours(23, 59, 59, 999);
                  }
                  onChange((prev) => ({ ...prev, validUntil: date }));
                  onClearError('validUntil');
                }}
              />
              {renderError(errors.validUntil)}
            </div>
          </div>
          <div className='flex flex-row items-center gap-2'>
            <Icon src={Info} size={IconSize.SIZE_20x20} color={ColorType.GREY6} />
            <Label
              text={t('promotion_uses_location_timezone')}
              type={LabelType.BODY3}
              color={ColorType.GREY6}
            />
          </div>
        </div>

        <div className='flex flex-col gap-3 py-5'>
          <div className='flex flex-col gap-1'>
            <Label text={t('value')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
            <Label
              text={t('set_value_for_promotion')}
              type={LabelType.BODY3}
              color={ColorType.GREY6}
            />
          </div>
          <div className='flex flex-row items-center gap-3'>
            <FormInput
              placeholder='0.00'
              inputType={InputType.NUMBER}
              validationType={ValidationType.PERCENT}
              defaultValue={state.discountValue}
              width='118px'
              maxLen='3'
              inputClass='border-0 focus:ring-0'
              hasError={Boolean(errors.discountValue)}
              props={{ step: '1', min: '1', max: '100' }}
              onChange={(event) => handleDiscountValueChange(event.target.value)}
              dataTestId='promotionValue'
            />
            <Label
              text={t('percent_off_total_session_cost')}
              type={LabelType.LABEL_M}
              color={ColorType.BLACK}
            />
          </div>
          {renderError(errors.discountValue)}
        </div>

        <div className='flex flex-col gap-3 py-5'>
          <div className='flex flex-col gap-1'>
            <Label text={t('applies_to')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
            <Label
              text={t('select_chargers_for_promotion')}
              type={LabelType.BODY3}
              color={ColorType.GREY6}
            />
          </div>
          <div className='w-full max-w-[440px]'>
            <Dropdown
              placeholder={t('select_chargers')}
              placeholderLabelType={LabelType.LABEL_S_GREY4}
              headerWidth='100%'
              items={state.chargers}
              contentDivHeight={300}
              type={DropdownType.CHECKBOX_TREE}
              showPillHeader
              onItemClick={(items: ChargerSelectionList[]) => handleChargersChange(items)}
              showFooter
              pillIcon={ChargerEV}
              placement='bottom-start'
              dataTestId='promotionChargers'
            />
            {renderError(errors.chargers)}
          </div>
        </div>

        <div className='flex flex-col gap-3 py-5'>
          <div className='flex flex-col gap-1'>
            <Label text={t('receipt_label')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
            <Label
              text={t('receipt_label_description')}
              type={LabelType.BODY3}
              color={ColorType.GREY6}
            />
          </div>
          {renderLanguageInput(PROMOTION_LANGUAGES.ENGLISH, false)}
          {OPTIONAL_PROMOTION_LANGUAGES.filter((lang) =>
            state.visibleOptionalLanguages.includes(lang),
          ).map((lang) => renderLanguageInput(lang, true))}
          <div className='flex flex-col gap-2'>
            {OPTIONAL_PROMOTION_LANGUAGES.filter(
              (lang) => !state.visibleOptionalLanguages.includes(lang),
            ).map((lang) => (
              <button
                key={lang}
                type='button'
                className='flex flex-row items-center gap-2 self-start'
                onClick={() => onShowSecondaryLanguage(lang)}
                data-testid={`addLabel-${lang}`}
              >
                <Icon src={AddPlus2} size={IconSize.SIZE_16x16} color={ColorType.DENIM} />
                <Label
                  text={t(LANGUAGE_ADD_KEY[lang])}
                  type={LabelType.LABEL_S}
                  color={ColorType.DENIM}
                />
              </button>
            ))}
          </div>
        </div>

        <div className='flex flex-col gap-4 py-5 !border-t-0'>
          <div className='flex flex-row items-start gap-2 rounded bg-warning_light px-3 py-2 w-fit'>
            <Icon src={ErrorWarningCircle} size={IconSize.SIZE_20x20} color={ColorType.WARNING2} />
            <p className='text-sm leading-5 text-grey6'>
              <Trans
                i18nKey='promotion_save_publish_notice'
                components={{ strong: <span className='font-semibold' /> }}
              />
            </p>
          </div>
          <div className='flex flex-row justify-end'>
            <Button
              label={t('save_promotion_details')}
              type={ButtonType.SECONDARY}
              size={ButtonSize.SMALL}
              onClick={onSave}
              isSumbit
              isLoading={isSaving}
              disabled={isSaving}
              dataTestId='savePromotionDetails'
            />
          </div>
        </div>
      </div>
    </div>
  );
};
